import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  FileText,
  Plus,
  Trash2,
  Edit2,
  Pin,
  PinOff,
  AtSign,
  Save,
  Loader2,
  Clock,
  MoreHorizontal,
  Bold,
  Italic,
  Underline,
  List,
  ListOrdered,
  Quote,
  Link,
  Image,
  Smile,
  X,
  ChevronDown,
  Search,
} from 'lucide-react';

interface NotesEditorProps {
  contactId: number;
  onNoteSaved?: () => void;
}

interface Note {
  id: number;
  content: string;
  content_html: string;
  mentions: Array<{ id: number; name: string }>;
  is_pinned: boolean;
  created_at: string;
  updated_at: string;
  author_name: string;
  author_avatar?: string;
}

interface User {
  id: number;
  name: string;
  email: string;
  avatar?: string;
}

// Simple rich text toolbar actions
const toolbarActions = [
  { icon: Bold, action: 'bold', label: 'Bold' },
  { icon: Italic, action: 'italic', label: 'Italic' },
  { icon: Underline, action: 'underline', label: 'Underline' },
  { icon: List, action: 'insertUnorderedList', label: 'Bullet List' },
  { icon: ListOrdered, action: 'insertOrderedList', label: 'Numbered List' },
  { icon: Quote, action: 'formatBlock', value: 'blockquote', label: 'Quote' },
];

export function NotesEditor({ contactId, onNoteSaved }: NotesEditorProps) {
  const [notes, setNotes] = useState<Note[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editingNote, setEditingNote] = useState<Note | null>(null);
  const [noteContent, setNoteContent] = useState('');
  const [isPinned, setIsPinned] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionIndex, setMentionIndex] = useState(0);
  const [users, setUsers] = useState<User[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [cursorPosition, setCursorPosition] = useState(0);

  // Fetch notes
  const fetchNotes = async () => {
    try {
      const response = await fetch(`/api/crm/communications/notes?contactId=${contactId}`);
      if (response.ok) {
        const data = await response.json();
        setNotes(data.notes);
      }
    } catch (error) {
      console.error('Failed to fetch notes:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch users for mentions
  const fetchUsers = async () => {
    try {
      const response = await fetch('/api/users'); // Assuming this endpoint exists
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      }
    } catch (error) {
      // Use mock users if endpoint doesn't exist
      setUsers([
        { id: 1, name: 'John Doe', email: 'john@example.com' },
        { id: 2, name: 'Jane Smith', email: 'jane@example.com' },
        { id: 3, name: 'Mike Johnson', email: 'mike@example.com' },
      ]);
    }
  };

  useEffect(() => {
    fetchNotes();
    fetchUsers();
  }, [contactId]);

  // Handle @ mention
  useEffect(() => {
    const lastAtIndex = noteContent.lastIndexOf('@', cursorPosition);
    if (lastAtIndex !== -1 && cursorPosition > lastAtIndex) {
      const searchTerm = noteContent.slice(lastAtIndex + 1, cursorPosition);
      if (!searchTerm.includes(' ')) {
        setMentionSearch(searchTerm);
        setShowMentions(true);
        setMentionIndex(0);
      } else {
        setShowMentions(false);
      }
    } else {
      setShowMentions(false);
    }
  }, [noteContent, cursorPosition]);

  const filteredUsers = users.filter(
    (u) =>
      u.name.toLowerCase().includes(mentionSearch.toLowerCase()) ||
      u.email.toLowerCase().includes(mentionSearch.toLowerCase())
  );

  const insertMention = (user: User) => {
    const lastAtIndex = noteContent.lastIndexOf('@', cursorPosition);
    const newContent =
      noteContent.slice(0, lastAtIndex) +
      `@[${user.name}](${user.id}) ` +
      noteContent.slice(cursorPosition);
    setNoteContent(newContent);
    setShowMentions(false);
    textareaRef.current?.focus();
  };

  const handleSave = async () => {
    if (!noteContent.trim()) return;

    setIsSaving(true);
    try {
      const url = editingNote
        ? `/api/crm/communications/notes/${editingNote.id}`
        : '/api/crm/communications/notes';
      const method = editingNote ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contactId,
          content: noteContent,
          contentHtml: formatContentToHtml(noteContent),
          isPinned,
        }),
      });

      if (response.ok) {
        setNoteContent('');
        setIsEditing(false);
        setEditingNote(null);
        setIsPinned(false);
        fetchNotes();
        onNoteSaved?.();
      }
    } catch (error) {
      console.error('Failed to save note:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (noteId: number) => {
    if (!confirm('Are you sure you want to delete this note?')) return;

    try {
      const response = await fetch(`/api/crm/communications/notes/${noteId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        fetchNotes();
      }
    } catch (error) {
      console.error('Failed to delete note:', error);
    }
  };

  const handleEdit = (note: Note) => {
    setEditingNote(note);
    setNoteContent(note.content);
    setIsPinned(note.is_pinned);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditingNote(null);
    setNoteContent('');
    setIsPinned(false);
  };

  const formatContentToHtml = (content: string): string => {
    // Convert @mentions to styled spans
    let html = content
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/__(.*?)__/g, '<u>$1</u>')
      .replace(/@\[([^\]]+)\]\((\d+)\)/g, '<span class="text-[#2B7FFF] bg-[#2B7FFF]/10 px-1 rounded">@$1</span>')
      .replace(/\n/g, '<br>');
    return html;
  };

  const renderContent = (content: string) => {
    const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    return esc(content).replace(
      /@\[([^\]]+)\]\((\d+)\)/g,
      '<span class="text-[#2B7FFF] bg-[#2B7FFF]/10 px-1.5 py-0.5 rounded font-medium">@$1</span>'
    );
  };

  const insertFormatting = (format: string, value?: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = noteContent.substring(start, end);
    let newText = '';

    switch (format) {
      case 'bold':
        newText = `**${selectedText || 'bold text'}**`;
        break;
      case 'italic':
        newText = `*${selectedText || 'italic text'}*`;
        break;
      case 'underline':
        newText = `__${selectedText || 'underlined text'}__`;
        break;
      case 'insertUnorderedList':
        newText = selectedText
          ? selectedText
              .split('\n')
              .map((line) => `- ${line}`)
              .join('\n')
          : '- list item';
        break;
      case 'insertOrderedList':
        newText = selectedText
          ? selectedText
              .split('\n')
              .map((line, i) => `${i + 1}. ${line}`)
              .join('\n')
          : '1. list item';
        break;
      default:
        newText = selectedText;
    }

    const newContent =
      noteContent.substring(0, start) + newText + noteContent.substring(end);
    setNoteContent(newContent);

    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + newText.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (isLoading) {
    return (
      <div className="h-64 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#2B7FFF]" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* New Note Button */}
      {!isEditing && (
        <Button
          onClick={() => setIsEditing(true)}
          className="w-full bg-[#2B7FFF] hover:bg-[#1A5FCC]"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add New Note
        </Button>
      )}

      {/* Editor */}
      <AnimatePresence>
        {isEditing && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <Card className="border-[#2B7FFF]/30">
              <CardContent className="p-4 space-y-3">
                {/* Toolbar */}
                <div className="flex items-center gap-1 pb-2 border-b flex-wrap">
                  {toolbarActions.map((action) => (
                    <button
                      key={action.action}
                      onClick={() => insertFormatting(action.action, action.value)}
                      className="p-1.5 rounded hover:bg-muted transition-colors"
                      title={action.label}
                    >
                      <action.icon className="h-4 w-4" />
                    </button>
                  ))}
                  <Separator orientation="vertical" className="h-6 mx-1" />
                  <button
                    onClick={() => {
                      const pos = textareaRef.current?.selectionStart || 0;
                      setCursorPosition(pos);
                      setNoteContent((prev) => prev.slice(0, pos) + '@' + prev.slice(pos));
                      textareaRef.current?.focus();
                    }}
                    className="p-1.5 rounded hover:bg-muted transition-colors text-[#2B7FFF]"
                    title="Mention user"
                  >
                    <AtSign className="h-4 w-4" />
                  </button>
                </div>

                {/* Textarea with mention dropdown */}
                <div className="relative">
                  <Textarea
                    ref={textareaRef}
                    value={noteContent}
                    onChange={(e) => {
                      setNoteContent(e.target.value);
                      setCursorPosition(e.target.selectionStart);
                    }}
                    onKeyUp={(e) => setCursorPosition(e.currentTarget.selectionStart)}
                    onClick={(e) => setCursorPosition(e.currentTarget.selectionStart)}
                    placeholder="Write a note... Use @ to mention team members"
                    className="min-h-[150px] resize-none"
                  />

                  {/* Mention Dropdown */}
                  {showMentions && filteredUsers.length > 0 && (
                    <div className="absolute z-50 w-64 mt-1 bg-popover border rounded-md shadow-lg">
                      <div className="p-2">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                          <Search className="h-3 w-3" />
                          <span>Mention a team member</span>
                        </div>
                        {filteredUsers.map((user, index) => (
                          <button
                            key={user.id}
                            onClick={() => insertMention(user)}
                            className={cn(
                              'w-full flex items-center gap-2 p-2 rounded text-left hover:bg-muted transition-colors',
                              index === mentionIndex && 'bg-muted'
                            )}
                          >
                            <div className="h-8 w-8 rounded-full bg-[#2B7FFF]/10 flex items-center justify-center text-[#2B7FFF] text-sm font-medium">
                              {user.name.split(' ').map((n) => n[0]).join('')}
                            </div>
                            <div>
                              <div className="font-medium text-sm">{user.name}</div>
                              <div className="text-xs text-muted-foreground">{user.email}</div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Options */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setIsPinned(!isPinned)}
                      className={cn(
                        'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors',
                        isPinned
                          ? 'bg-amber-500/10 text-amber-600'
                          : 'hover:bg-muted text-muted-foreground'
                      )}
                    >
                      {isPinned ? (
                        <>
                          <Pin className="h-4 w-4" />
                          Pinned
                        </>
                      ) : (
                        <>
                          <PinOff className="h-4 w-4" />
                          Pin Note
                        </>
                      )}
                    </button>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={handleCancel}>
                      <X className="h-4 w-4 mr-2" />
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleSave}
                      disabled={isSaving || !noteContent.trim()}
                      className="bg-[#2B7FFF] hover:bg-[#1A5FCC]"
                    >
                      {isSaving ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4 mr-2" />
                      )}
                      {editingNote ? 'Update' : 'Save'} Note
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Notes List */}
      <div className="space-y-3">
        {notes.length === 0 ? (
          <div className="text-center py-12">
            <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
              <FileText className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-medium">No notes yet</h3>
            <p className="text-muted-foreground text-sm mt-1">
              Add notes to keep track of important information
            </p>
          </div>
        ) : (
          notes.map((note, index) => (
            <motion.div
              key={note.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card
                className={cn(
                  'group transition-all hover:shadow-md',
                  note.is_pinned && 'border-amber-500/30 bg-amber-50/5'
                )}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 rounded-full bg-[#2B7FFF]/10 flex items-center justify-center text-[#2B7FFF] text-sm font-medium">
                        {note.author_name?.split(' ').map((n) => n[0]).join('') || 'U'}
                      </div>
                      <div>
                        <div className="font-medium text-sm">{note.author_name || 'Unknown'}</div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Clock className="h-3 w-3" />
                          {formatRelativeTime(note.created_at)}
                          {note.is_pinned && (
                            <Badge
                              variant="secondary"
                              className="bg-amber-500/10 text-amber-600 text-[10px]"
                            >
                              <Pin className="h-3 w-3 mr-1" />
                              Pinned
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleEdit(note)}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => handleDelete(note.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <div
                    className="mt-3 text-sm whitespace-pre-wrap prose prose-sm max-w-none dark:prose-invert"
                    dangerouslySetInnerHTML={{ __html: renderContent(note.content) }}
                  />

                  {/* Mentions */}
                  {note.mentions && note.mentions.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-3 pt-3 border-t">
                      <span className="text-xs text-muted-foreground">Mentions:</span>
                      {note.mentions.map((mention) => (
                        <Badge
                          key={mention.id}
                          variant="secondary"
                          className="text-xs bg-[#2B7FFF]/10 text-[#2B7FFF]"
                        >
                          @{mention.name}
                        </Badge>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
}

export default NotesEditor;
